import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaChartBar,
  FaFilter,
  FaListAlt,
  FaSearch,
  FaSyncAlt,
  FaQuestionCircle,
  FaInfoCircle,
  FaComments,
  FaChevronDown,
  FaChevronUp,
  FaGlobe
} from 'react-icons/fa';
import SearchableDropdown from './shared/SearchableDropdown';
import { trackEvent } from '../services/applicationInsights';
import { API_BASE_URL, getHeaders } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import { showToast } from '../utils/toast';

const DEFAULT_PAGE_SIZE = 50;

const getGlobalObject = () => {
  if (typeof window !== 'undefined') return window;
  if (typeof global !== 'undefined') return global;
  try {
    return Function('return this')();
  } catch (e) {
    return {};
  }
};

const globalObj = getGlobalObject();

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  const getGlobalFetch = () => {
    if (typeof global !== 'undefined' && global.fetch && global.fetch !== fetchWithRetry) {
      return global.fetch;
    }
    if (typeof window !== 'undefined' && window.fetch && window.fetch !== fetchWithRetry) {
      return window.fetch;
    }
    if (globalObj && globalObj.fetch && globalObj.fetch !== fetchWithRetry) {
      return globalObj.fetch;
    }
    return (globalObj && globalObj.fetch) || window.fetch || global.fetch;
  };

  const realFetch = getGlobalFetch();

  for (let i = 0; i < retries; i++) {
    try {
      const res = await realFetch(url, options);
      if (!res.ok && [502, 503, 504].includes(res.status)) {
        throw new Error(`Server returned transient status: ${res.status}`);
      }
      return res;
    } catch (err) {
      if (options.signal?.aborted || err?.name === 'AbortError') {
        throw err;
      }
      if (i === retries - 1) {
        throw err;
      }
      console.warn(`[fetchWithRetry] Attempt ${i + 1} failed. Retrying in ${delay}ms...`, err);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const fetch = fetchWithRetry;

function safeNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function formatRate(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }

  return `${Number(value).toFixed(2)}%`;
}

function isMissingRateValue(value) {
  return value === null || value === undefined || Number.isNaN(Number(value));
}

function formatDateTime(value) {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }

  return date.toLocaleString();
}

function normalizeRole(role) {
  return String(role || '').trim().toLowerCase();
}

export default function SurveyInsights({ token, roles = [], orgId }) {
  const normalizedRoles = useMemo(() => roles.map(normalizeRole), [roles]);
  const isSystemAdmin = normalizedRoles.includes('systemadministrator');
  const canViewSurveyInsights = normalizedRoles.some((role) => (
    role === 'systemadministrator' ||
    role === 'organizationadministrator' ||
    role === 'sitecollectionowner'
  ));

  const fetchOrgsAbortRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [siteCollections, setSiteCollections] = useState([]);
  const [flowOptions, setFlowOptions] = useState([]);

  const [selectedOrg, setSelectedOrg] = useState(() => {
    return isSystemAdmin ? '' : (orgId || '');
  });

  useEffect(() => {
    return () => {
      fetchOrgsAbortRef.current?.abort();
      fetchSitesAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedFlow, setSelectedFlow] = useState('');

  const [overview, setOverview] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [surveyDetail, setSurveyDetail] = useState(null);
  const [surveyTrend, setSurveyTrend] = useState([]);

  const [surveyResponses, setSurveyResponses] = useState([]);
  const [surveyResponsesContinuationToken, setSurveyResponsesContinuationToken] = useState(null);
  const [hasMoreSurveyResponses, setHasMoreSurveyResponses] = useState(false);

  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [questionSummary, setQuestionSummary] = useState(null);
  const [questionResponses, setQuestionResponses] = useState([]);
  const [questionResponsesContinuationToken, setQuestionResponsesContinuationToken] = useState(null);
  const [hasMoreQuestionResponses, setHasMoreQuestionResponses] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [showSurveyResponses, setShowSurveyResponses] = useState(true);
  const [showQuestionResponses, setShowQuestionResponses] = useState(true);

  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [loadingSurveyDetail, setLoadingSurveyDetail] = useState(false);
  const [loadingSurveyResponses, setLoadingSurveyResponses] = useState(false);
  const [loadingQuestionSummary, setLoadingQuestionSummary] = useState(false);
  const [loadingQuestionResponses, setLoadingQuestionResponses] = useState(false);
  const [loadingTrend, setLoadingTrend] = useState(false);



  const authHeaders = useMemo(() => getHeaders(token), [token]);

  const clearFlowAndSurveyData = useCallback(() => {
    setFlowOptions([]);
    setSelectedFlow('');
    setSurveys([]);
    setSelectedSurveyId('');
    setSurveyDetail(null);
    setSurveyTrend([]);
    setSelectedQuestionId('');
    setQuestionSummary(null);
    setSurveyResponses([]);
    setSurveyResponsesContinuationToken(null);
    setHasMoreSurveyResponses(false);
    setQuestionResponses([]);
    setQuestionResponsesContinuationToken(null);
    setHasMoreQuestionResponses(false);
  }, []);

  const parseOrganizationError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view organizations.',
      500: 'Unable to load organizations at the moment.',
      default: 'Failed to load organizations.'
    });
  }, []);

  const parseSiteError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid organization context for site collections.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view site collections.',
      404: 'No site collections found for the selected organization.',
      500: 'Unable to load site collections.',
      default: 'Failed to load site collections.'
    });
  }, []);

  const parseSurveyInsightsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid survey insights query. Please verify selected filters.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'Access denied for the selected survey insights scope.',
      404: 'No survey insight data was found for the selected context.',
      500: 'An unexpected server error occurred while loading survey insights.',
      default: 'Unable to load survey insights data.'
    });
  }, []);

  const fetchOrganizations = useCallback(async () => {
    if (!isSystemAdmin) {
      return;
    }

    fetchOrgsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchOrgsAbortRef.current = controller;

    setLoadingOrganizations(true);
    try {
      const res = await fetch(`${API_BASE_URL}/organizations`, {
        headers: authHeaders,
        signal: controller.signal
      });
      if (!res.ok) {
        throw new Error(await parseOrganizationError(res));
      }

      const data = await res.json().catch(() => []);
      const organizationItems = Array.isArray(data) ? data : [];
      setOrganizations(organizationItems);

      setSelectedOrg((prev) => {
        if (prev && !organizationItems.some((o) => o.id === prev)) {
          return '';
        }
        return prev;
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err?.message || 'Failed to load organizations.');
      setOrganizations([]);
    } finally {
      if (fetchOrgsAbortRef.current === controller) {
        setLoadingOrganizations(false);
      }
    }
  }, [authHeaders, isSystemAdmin, parseOrganizationError]);

  const fetchSiteCollections = useCallback(async (organizationId) => {
    if (isSystemAdmin && !organizationId) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      return;
    }

    fetchSitesAbortRef.current?.abort();
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    setLoadingSites(true);
    try {
      const endpoint = isSystemAdmin
        ? `${API_BASE_URL}/site-collections?organizationId=${organizationId}`
        : `${API_BASE_URL}/site-collections`;
      const res = await fetch(endpoint, {
        headers: authHeaders,
        signal: controller.signal
      });

      if (!res.ok) {
        const message = await parseSiteError(res);
        if (res.status === 404) {
          setSiteCollections([]);
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => []);
      const list = Array.isArray(data) ? data : [];
      setSiteCollections(list);
      setSelectedSite((prev) => {
        if (prev && !list.some((s) => s.id === prev)) {
          return '';
        }
        return prev;
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err?.message || 'Failed to load site collections.');
      setSiteCollections([]);
    } finally {
      if (fetchSitesAbortRef.current === controller) {
        setLoadingSites(false);
      }
    }
  }, [authHeaders, isSystemAdmin, parseSiteError]);

  const fetchSiteOverview = useCallback(async (siteId) => {
    if (!siteId) {
      setOverview(null);
      setFlowOptions([]);
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingOverview(true);
    try {      
      const res = await fetch(`${API_BASE_URL}/survey-insights/site-collections/${siteId}/overview?organizationId=${orgIdToUse}`, {
        headers: authHeaders
      });

      if (!res.ok) {
        const message = await parseSurveyInsightsError(res);
        if (res.status === 404) {
          setOverview(null);
          try {
            const flowsRes = await fetch(`${API_BASE_URL}/userflows/site-collections/${siteId}?organizationId=${orgIdToUse}`, {
              headers: authHeaders
            });
            if (flowsRes.ok) {
              const flowsData = await flowsRes.json().catch(() => []);
              const mappedFlows = (Array.isArray(flowsData) ? flowsData : []).map(f => ({
                id: f.id,
                name: f.flowName || f.id,
                status: f.status || 'Active',
                surveyCount: 0,
                surveySubmissionCount: 0,
                surveyCompletionRate: null
              }));
              setFlowOptions(mappedFlows);
            } else {
              setFlowOptions([]);
            }
          } catch (err) {
            console.warn('Failed to load fallback flows list:', err);
            setFlowOptions([]);
          }
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => null);
      setOverview(data);

      const flows = Array.isArray(data?.flows)
        ? data.flows.map((flow) => ({
          id: flow.flowId,
          name: flow.flowName || flow.flowId,
          status: flow.flowStatus,
          surveyCount: flow.surveyCount,
          surveySubmissionCount: flow.surveySubmissionCount,
          surveyCompletionRate: flow.surveyCompletionRate
        }))
        : [];

      setFlowOptions(flows);
      setSelectedFlow('');
      setSurveys([]);
      setSelectedSurveyId('');
      setSurveyDetail(null);
      setSurveyTrend([]);
      setSelectedQuestionId('');
      setQuestionSummary(null);
      setSurveyResponses([]);
      setQuestionResponses([]);
    } catch (err) {
      showToast.error(err?.message || 'Failed to load survey overview.');
      setOverview(null);
      setFlowOptions([]);
    } finally {
      setLoadingOverview(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchSurveysForFlow = useCallback(async (siteId, flowId) => {
    if (!siteId || !flowId) {
      setSurveys([]);
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingSurveys(true);
    try {
      const res = await fetch(`${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys?organizationId=${orgIdToUse}`, {
        headers: authHeaders
      });

      if (!res.ok) {
        const message = await parseSurveyInsightsError(res);
        if (res.status === 404) {
          setSurveys([]);
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => ({}));
      const result = Array.isArray(data?.surveys) ? data.surveys : [];
      setSurveys(result);
      setSelectedSurveyId('');
      setSurveyDetail(null);
      setSurveyTrend([]);
      setSelectedQuestionId('');
      setQuestionSummary(null);
      setSurveyResponses([]);
      setQuestionResponses([]);
    } catch (err) {
      showToast.error(err?.message || 'Failed to load surveys for selected flow.');
      setSurveys([]);
    } finally {
      setLoadingSurveys(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchSurveyDetail = useCallback(async (siteId, flowId, stepId) => {
    if (!siteId || !flowId || !stepId) {
      setSurveyDetail(null);
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingSurveyDetail(true);
    try {
      const res = await fetch(`${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys/${stepId}?organizationId=${orgIdToUse}`, {
        headers: authHeaders
      });

      if (!res.ok) {
        throw new Error(await parseSurveyInsightsError(res));
      }

      const data = await res.json().catch(() => null);
      setSurveyDetail(data);

      const firstQuestionId = data?.survey?.questions?.[0]?.questionId || '';
      setSelectedQuestionId(firstQuestionId);
    } catch (err) {
      showToast.error(err?.message || 'Failed to load survey details.');
      setSurveyDetail(null);
      setSelectedQuestionId('');
    } finally {
      setLoadingSurveyDetail(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchSurveyTrend = useCallback(async (siteId, flowId, stepId) => {
    if (!siteId || !flowId || !stepId) {
      setSurveyTrend([]);
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingTrend(true);
    try {
      const res = await fetch(`${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys/${stepId}/trend?organizationId=${orgIdToUse}&bucket=day`, {
        headers: authHeaders
      });

      if (!res.ok) {
        const message = await parseSurveyInsightsError(res);
        if (res.status === 404) {
          setSurveyTrend([]);
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => ({}));
      setSurveyTrend(Array.isArray(data?.points) ? data.points : []);
    } catch (err) {
      showToast.error(err?.message || 'Failed to load survey trend.');
      setSurveyTrend([]);
    } finally {
      setLoadingTrend(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchSurveyResponses = useCallback(async ({ siteId, flowId, stepId, continuationToken = null, append = false }) => {
    if (!siteId || !flowId || !stepId) {
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingSurveyResponses(true);
    try {
      const params = new URLSearchParams({
        pageSize: String(DEFAULT_PAGE_SIZE),
        organizationId: orgIdToUse
      });

      if (continuationToken) {
        params.set('continuationToken', continuationToken);
      }

      const res = await fetch(
        `${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys/${stepId}/responses?${params.toString()}`,
        { headers: authHeaders }
      );

      if (!res.ok) {
        throw new Error(await parseSurveyInsightsError(res));
      }

      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data?.items) ? data.items : [];

      setSurveyResponses((current) => (append ? [...current, ...items] : items));
      setSurveyResponsesContinuationToken(data?.continuationToken || null);
      setHasMoreSurveyResponses(Boolean(data?.hasMore));
    } catch (err) {
      showToast.error(err?.message || 'Failed to load survey responses.');
      if (!append) {
        setSurveyResponses([]);
      }
      setSurveyResponsesContinuationToken(null);
      setHasMoreSurveyResponses(false);
    } finally {
      setLoadingSurveyResponses(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchQuestionSummary = useCallback(async (siteId, flowId, stepId, questionId) => {
    if (!siteId || !flowId || !stepId || !questionId) {
      setQuestionSummary(null);
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingQuestionSummary(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys/${stepId}/questions/${questionId}/summary?organizationId=${orgIdToUse}`,
        { headers: authHeaders }
      );

      if (!res.ok) {
        throw new Error(await parseSurveyInsightsError(res));
      }

      const data = await res.json().catch(() => null);
      setQuestionSummary(data);
    } catch (err) {
      showToast.error(err?.message || 'Failed to load question summary.');
      setQuestionSummary(null);
    } finally {
      setLoadingQuestionSummary(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  const fetchQuestionResponses = useCallback(async ({
    siteId,
    flowId,
    stepId,
    questionId,
    continuationToken = null,
    append = false
  }) => {
    if (!siteId || !flowId || !stepId || !questionId) {
      return;
    }

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    setLoadingQuestionResponses(true);
    try {
      const params = new URLSearchParams({
        pageSize: String(DEFAULT_PAGE_SIZE),
        organizationId: orgIdToUse
      });

      if (continuationToken) {
        params.set('continuationToken', continuationToken);
      }

      const res = await fetch(
        `${API_BASE_URL}/survey-insights/site-collections/${siteId}/userflows/${flowId}/surveys/${stepId}/questions/${questionId}/responses?${params.toString()}`,
        { headers: authHeaders }
      );

      if (!res.ok) {
        throw new Error(await parseSurveyInsightsError(res));
      }

      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data?.items) ? data.items : [];

      setQuestionResponses((current) => (append ? [...current, ...items] : items));
      setQuestionResponsesContinuationToken(data?.continuationToken || null);
      setHasMoreQuestionResponses(Boolean(data?.hasMore));
    } catch (err) {
      showToast.error(err?.message || 'Failed to load question responses.');
      if (!append) {
        setQuestionResponses([]);
      }
      setQuestionResponsesContinuationToken(null);
      setHasMoreQuestionResponses(false);
    } finally {
      setLoadingQuestionResponses(false);
    }
  }, [authHeaders, parseSurveyInsightsError, isSystemAdmin, selectedOrg, orgId]);

  useEffect(() => {
    if (!canViewSurveyInsights) {
      return;
    }

    fetchOrganizations();
  }, [canViewSurveyInsights, fetchOrganizations]);

  useEffect(() => {
    if (!canViewSurveyInsights) {
      return;
    }

    const targetOrg = isSystemAdmin ? selectedOrg : orgId;
    if (!targetOrg) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      setSelectedSite('');
      setOverview(null);
      clearFlowAndSurveyData();
      setLoadingSites(false);
      return;
    }

    fetchSiteCollections(targetOrg);
  }, [canViewSurveyInsights, clearFlowAndSurveyData, fetchSiteCollections, isSystemAdmin, orgId, selectedOrg]);

  useEffect(() => {
    if (!selectedSite) {
      setOverview(null);
      clearFlowAndSurveyData();
      return;
    }

    fetchSiteOverview(selectedSite);
  }, [clearFlowAndSurveyData, fetchSiteOverview, selectedSite]);

  useEffect(() => {
    if (!selectedSite || !selectedFlow) {
      setSurveys([]);
      setSelectedSurveyId('');
      setSurveyDetail(null);
      setSurveyTrend([]);
      setSelectedQuestionId('');
      setQuestionSummary(null);
      setSurveyResponses([]);
      setQuestionResponses([]);
      return;
    }

    fetchSurveysForFlow(selectedSite, selectedFlow);
  }, [fetchSurveysForFlow, selectedFlow, selectedSite]);

  useEffect(() => {
    if (!selectedSite || !selectedFlow || !selectedSurveyId) {
      setSurveyDetail(null);
      setSurveyTrend([]);
      setSelectedQuestionId('');
      setQuestionSummary(null);
      setSurveyResponses([]);
      setQuestionResponses([]);
      return;
    }

    fetchSurveyDetail(selectedSite, selectedFlow, selectedSurveyId);
    fetchSurveyTrend(selectedSite, selectedFlow, selectedSurveyId);
    fetchSurveyResponses({
      siteId: selectedSite,
      flowId: selectedFlow,
      stepId: selectedSurveyId,
      continuationToken: null,
      append: false
    });
  }, [
    fetchSurveyDetail,
    fetchSurveyResponses,
    fetchSurveyTrend,
    selectedFlow,
    selectedSite,
    selectedSurveyId
  ]);

  useEffect(() => {
    if (!selectedQuestionId || !selectedSurveyId || !selectedSite || !selectedFlow) {
      setQuestionSummary(null);
      setQuestionResponses([]);
      setQuestionResponsesContinuationToken(null);
      setHasMoreQuestionResponses(false);
      return;
    }

    fetchQuestionSummary(selectedSite, selectedFlow, selectedSurveyId, selectedQuestionId);
    fetchQuestionResponses({
      siteId: selectedSite,
      flowId: selectedFlow,
      stepId: selectedSurveyId,
      questionId: selectedQuestionId,
      continuationToken: null,
      append: false
    });
  }, [
    fetchQuestionResponses,
    fetchQuestionSummary,
    selectedFlow,
    selectedQuestionId,
    selectedSite,
    selectedSurveyId
  ]);

  const selectedSurvey = useMemo(
    () => surveys.find((survey) => String(survey?.stepId) === String(selectedSurveyId)) || null,
    [selectedSurveyId, surveys]
  );

  const surveyQuestions = useMemo(() => {
    const items = surveyDetail?.survey?.questions;
    return Array.isArray(items) ? items : [];
  }, [surveyDetail]);

  const filteredSurveys = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return surveys;
    }

    return surveys.filter((survey) => {
      const inTitle = String(survey?.surveyTitle || '').toLowerCase().includes(query);
      const inStepName = String(survey?.stepName || '').toLowerCase().includes(query);
      const inStepId = String(survey?.stepId || '').toLowerCase().includes(query);
      const inQuestionTypes = Array.isArray(survey?.questionTypes)
        ? survey.questionTypes.join(' ').toLowerCase().includes(query)
        : false;

      return inTitle || inStepName || inStepId || inQuestionTypes;
    });
  }, [searchTerm, surveys]);

  const totals = useMemo(() => {
    const source = overview?.totals || {};
    return {
      flowWithSurveyCount: safeNumber(source.flowWithSurveyCount ?? source.flowCount),
      surveyStepCount: safeNumber(source.surveyStepCount),
      surveySubmissionCount: safeNumber(source.surveySubmissionCount),
      surveyCompletionRate: source.surveyCompletionRate
    };
  }, [overview]);

  const canLoadMoreSurveyResponses = hasMoreSurveyResponses && Boolean(surveyResponsesContinuationToken);
  const canLoadMoreQuestionResponses = hasMoreQuestionResponses && Boolean(questionResponsesContinuationToken);
  const isCompletionRateMissing = isMissingRateValue(totals.surveyCompletionRate);

  if (!canViewSurveyInsights) {
    return (
      <div style={wrapperStyle}>
        <div style={containerStyle}>
          <div style={blockingStateStyle}>
            <h2 style={blockingTitleStyle}>Survey insights is not available for your role.</h2>
            <p style={blockingMessageStyle}>
              Only System Administrator, Organization Administrator, and Site Collection Owner roles can access this area.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaChartBar size={20} /></div>
            <div>
              <h1 style={titleStyle}>Survey Insights</h1>
              <div style={subtitleStyle}>Explore surveys by site and flow, then drill down into question-level responses.</div>
            </div>
          </div>
        </header>

        <section style={kpiRowStyle}>
          <article style={kpiCardStyle}>
            <div style={kpiLabelStyle}>Flows With Survey</div>
            <div style={kpiValueStyle}>{totals.flowWithSurveyCount}</div>
          </article>
          <article style={kpiCardStyle}>
            <div style={kpiLabelStyle}>Step count with Survey</div>
            <div style={kpiValueStyle}>{totals.surveyStepCount}</div>
          </article>
          <article style={kpiCardStyle}>
            <div style={kpiLabelStyle}>Survey Submissions</div>
            <div style={kpiValueStyle}>{totals.surveySubmissionCount}</div>
          </article>
          <article style={kpiCardStyle}>
            <div style={{ ...kpiLabelStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Completion Rate</span>
              {isCompletionRateMissing && (
                <span
                  title="Completion Rate will be unavailable if rendered telemetry is missing for the selected scope."
                  aria-label="Completion rate information"
                  style={infoTooltipIconStyle}
                >
                  <FaInfoCircle />
                </span>
              )}
            </div>
            <div style={kpiValueStyle}>{formatRate(totals.surveyCompletionRate)}</div>
          </article>
        </section>

        <section style={filtersCardStyle}>
          <div style={cardHeaderStyle}>
            <FaFilter color="#6366f1" />
            <span style={cardTitleStyle}>Selection Criteria</span>
          </div>

          <div style={filtersGridStyle}>
            {isSystemAdmin && (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(value) => {
                    setSelectedOrg(value);
                    setSelectedSite('');
                    clearFlowAndSurveyData();
                    if (value && selectedSite) {
                      trackEvent('Dropdown Selected', {
                        section: 'survey-insights',
                        organizationId: value,
                        siteCollectionId: selectedSite
                      });
                    }
                  }}
                  placeholder="Choose Organization"
                  displayKey="name"
                  valueKey="id"
                  isLoading={loadingOrganizations}
                  disabled={loadingOrganizations}
                  styles={dropdownStyles}
                />
              </div>
            )}

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Site Collection</label>
              <SearchableDropdown
                options={siteCollections}
                selectedValue={selectedSite}
                onSelect={(value) => {
                  setSelectedSite(value);
                  clearFlowAndSurveyData();
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'survey-insights',
                      organizationId: orgIdToUse,
                      siteCollectionId: value
                    });
                  }
                }}
                placeholder={isSystemAdmin && !selectedOrg ? 'Select Org First' : 'Choose Site Collection'}
                displayKey="name"
                valueKey="id"
                isLoading={loadingSites}
                disabled={loadingSites || (isSystemAdmin && !selectedOrg)}
                styles={dropdownStyles}
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Flow</label>
              <SearchableDropdown
                options={flowOptions}
                selectedValue={selectedFlow}
                onSelect={(value) => {
                  setSelectedFlow(value);
                  setSelectedSurveyId('');
                  setSurveyDetail(null);
                  setSelectedQuestionId('');
                  setQuestionSummary(null);
                  setSurveyResponses([]);
                  setQuestionResponses([]);
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && selectedSite && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'survey-insights',
                      organizationId: orgIdToUse,
                      siteCollectionId: selectedSite,
                      flowId: value
                    });
                  }
                }}
                placeholder="Choose Flow"
                displayKey="name"
                valueKey="id"
                isLoading={loadingOverview}
                disabled={loadingOverview || !selectedSite}
                styles={dropdownStyles}
              />
            </div>

            <div style={{ ...inputGroupStyle, justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{
                  ...primaryButtonStyle,
                  opacity: !selectedSite || !selectedFlow || loadingSurveys ? 0.6 : 1,
                  cursor: !selectedSite || !selectedFlow || loadingSurveys ? 'not-allowed' : 'pointer'
                }}
                onClick={() => fetchSurveysForFlow(selectedSite, selectedFlow)}
                disabled={!selectedSite || !selectedFlow || loadingSurveys}
              >
                <FaSyncAlt style={{ animation: loadingSurveys ? 'spin 1s linear infinite' : 'none' }} />
                {loadingSurveys ? 'Loading Surveys...' : 'Refresh Surveys'}
              </button>
            </div>
          </div>
        </section>

        <section style={tableCardStyle}>
          <div style={tableActionsHeader}>
            <div style={tableToolbarGrid}>
              <div style={searchWrapper}>
                <FaSearch style={searchIconStyle} />
                <input
                  style={searchInputStyle}
                  placeholder="Search surveys..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  disabled={!selectedFlow}
                />
              </div>
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Survey</th>
                <th style={thStyle}>Questions</th>
                <th style={thStyle}>Responses</th>
                <th style={thStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>Completion</span>
                    <span
                      title="Completion Rate will be unavailable if rendered telemetry is missing for the selected scope."
                      aria-label="Completion rate information"
                      style={infoTooltipIconStyle}
                    >
                      <FaInfoCircle />
                    </span>
                  </span>
                </th>
                <th style={thActionStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingSurveys ? (
                <tr>
                  <td colSpan="5" style={emptyStateStyle}><FaSyncAlt className="spin" /> Loading surveys...</td>
                </tr>
              ) : filteredSurveys.length === 0 ? (
                <tr>
                  <td colSpan="5" style={emptyStateStyle}>
                    {isSystemAdmin && !selectedOrg
                      ? 'Select an organization to begin.'
                      : !selectedSite
                        ? 'Select a site collection to begin.'
                        : !selectedFlow
                          ? 'Select a flow to view its surveys.'
                          : 'No surveys found for the selected flow.'}
                  </td>
                </tr>
              ) : (
                filteredSurveys.map((survey) => {
                  const isSelected = String(selectedSurveyId) === String(survey.stepId);

                  return (
                    <tr key={survey.stepId} style={{ ...trStyle, background: isSelected ? '#f8faff' : '#ffffff' }}>
                      <td style={tdStyle}>
                        <div style={primaryTextStyle}>{survey.surveyTitle || survey.stepName || 'Untitled Survey'}</div>
                        {survey.stepName && (
                          <div style={secondaryTextStyle}>Step Name: {survey.stepName}</div>
                        )}
                        <div style={secondaryTextStyle}>Step ID/Survey ID: {survey.stepId || '--'}</div>
                      </td>
                      <td style={tdStyle}>{safeNumber(survey.questionCount)}</td>
                      <td style={tdStyle}>{safeNumber(survey.responseCount)}</td>
                      <td style={tdStyle}>{formatRate(survey.completionRate)}</td>
                      <td style={tdActionStyle}>
                        <button
                          type="button"
                          style={selectBtnStyle}
                          onClick={() => setSelectedSurveyId(survey.stepId)}
                        >
                          {isSelected ? 'Selected' : 'Inspect'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {selectedSurvey && (
          <section style={detailSectionStyle}>
            <div style={detailHeaderStyle}>
              <div>
                <h2 style={detailTitleStyle}>{selectedSurvey.surveyTitle || selectedSurvey.stepName || 'Survey Detail'}</h2>
                <div style={detailSubtitleStyle}>Flow-level drilldown for survey step {selectedSurvey.stepId}</div>
              </div>
            </div>

            <div style={metricsRowStyle}>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Renders</div>
                <div style={metricValueStyle}>{safeNumber(surveyDetail?.metrics?.renderCount)}</div>
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Submissions</div>
                <div style={metricValueStyle}>{safeNumber(surveyDetail?.metrics?.submissionCount)}</div>
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Unique Responders</div>
                <div style={metricValueStyle}>{safeNumber(surveyDetail?.metrics?.uniqueResponders)}</div>
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Completion</div>
                <div style={metricValueStyle}>{formatRate(surveyDetail?.metrics?.completionRate)}</div>
              </div>
            </div>

            <div style={surveyPanelsGridStyle}>
              <div style={panelCardStyle}>
                <div style={panelHeaderStyle}><FaQuestionCircle /> Questions</div>

                {loadingSurveyDetail ? (
                  <div style={panelEmptyStyle}>Loading survey questions...</div>
                ) : surveyQuestions.length === 0 ? (
                  <div style={panelEmptyStyle}>No questions found for this survey step.</div>
                ) : (
                  <div style={questionListStyle}>
                    {surveyQuestions.map((question) => {
                      const isQuestionSelected = String(selectedQuestionId) === String(question.questionId);
                      return (
                        <button
                          key={question.questionId}
                          type="button"
                          style={{
                            ...questionBtnStyle,
                            ...(isQuestionSelected ? questionBtnActiveStyle : null)
                          }}
                          onClick={() => setSelectedQuestionId(question.questionId)}
                        >
                          <div style={questionTitleStyle}>{question.question}</div>
                          <div style={questionMetaStyle}>{question.type}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={panelCardStyle}>
                <div style={panelHeaderStyle}><FaListAlt /> Question Summary</div>

                {!selectedQuestionId ? (
                  <div style={panelEmptyStyle}>Select a question to view summary and responses.</div>
                ) : loadingQuestionSummary ? (
                  <div style={panelEmptyStyle}>Loading summary...</div>
                ) : !questionSummary ? (
                  <div style={panelEmptyStyle}>Summary is unavailable for the selected question.</div>
                ) : (
                  <>
                    <div style={questionSummaryTitleStyle}>{questionSummary.question}</div>
                    <div style={summaryMetricsStyle}>
                      <div style={summaryMetricStyle}>Responses: <b>{safeNumber(questionSummary.responseCount)}</b></div>
                      <div style={summaryMetricStyle}>Average: <b>{questionSummary.average ?? '--'}</b></div>
                      <div style={summaryMetricStyle}>Median: <b>{questionSummary.median ?? '--'}</b></div>
                      <div style={summaryMetricStyle}>P90: <b>{questionSummary.p90 ?? '--'}</b></div>
                    </div>

                    <div style={distributionTableWrapStyle}>
                      <table style={distributionTableStyle}>
                        <thead>
                          <tr>
                            <th style={distributionThStyle}>Answer</th>
                            <th style={distributionThStyle}>Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(Array.isArray(questionSummary.distribution) ? questionSummary.distribution : []).map((entry) => (
                            <tr key={`${entry.value}-${entry.count}`}>
                              <td style={distributionTdStyle}>{entry.value}</td>
                              <td style={distributionTdStyle}>{safeNumber(entry.count)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={trendCardStyle}>
              <div style={panelHeaderStyle}><FaGlobe /> Render and Completion Trend</div>
              {loadingTrend ? (
                <div style={panelEmptyStyle}>Loading trend...</div>
              ) : surveyTrend.length === 0 ? (
                <div style={panelEmptyStyle}>No trend data available for this survey step.</div>
              ) : (
                <div style={distributionTableWrapStyle}>
                  <table style={distributionTableStyle}>
                    <thead>
                      <tr>
                        <th style={distributionThStyle}>Timestamp</th>
                        <th style={distributionThStyle}>Renders</th>
                        <th style={distributionThStyle}>Submissions</th>
                        <th style={distributionThStyle}>Completion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surveyTrend.map((point) => (
                        <tr key={`${point.timestamp}-${point.renderCount}-${point.submissionCount}`}>
                          <td style={distributionTdStyle}>{formatDateTime(point.timestamp)}</td>
                          <td style={distributionTdStyle}>{safeNumber(point.renderCount)}</td>
                          <td style={distributionTdStyle}>{safeNumber(point.submissionCount)}</td>
                          <td style={distributionTdStyle}>{formatRate(point.completionRate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={responsesGridStyle}>
              <div style={panelCardStyle}>
                <button
                  type="button"
                  style={expandToggleStyle}
                  onClick={() => setShowSurveyResponses((state) => !state)}
                >
                  <span style={panelHeaderStyle}><FaComments /> Survey Responses (All Questions)</span>
                  {showSurveyResponses ? <FaChevronUp /> : <FaChevronDown />}
                </button>

                {showSurveyResponses && (
                  <>
                    {loadingSurveyResponses && surveyResponses.length === 0 ? (
                      <div style={panelEmptyStyle}>Loading responses...</div>
                    ) : surveyResponses.length === 0 ? (
                      <div style={panelEmptyStyle}>No responses found for this survey.</div>
                    ) : (
                      <>
                        <div style={responseListStyle}>
                          {surveyResponses.map((item) => (
                            <div key={item.responseId || `${item.sessionId}-${item.submittedAt}`} style={responseCardStyle}>
                              <div style={responseMetaStyle}>
                                <span>Session: {item.sessionId || '--'}</span>
                                <span>{formatDateTime(item.submittedAt)}</span>
                              </div>
                              <div style={responseAnswerListStyle}>
                                {(Array.isArray(item.answers) ? item.answers : []).map((answer) => (
                                  <div key={`${answer.questionId}-${String(answer.answer)}`} style={responseAnswerItemStyle}>
                                    <div style={responseAnswerQuestionStyle}>{answer.question}</div>
                                    <div style={responseAnswerValueStyle}>{String(answer.answer ?? '--')}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          style={{
                            ...loadMoreBtnStyle,
                            opacity: canLoadMoreSurveyResponses && !loadingSurveyResponses ? 1 : 0.6,
                            cursor: canLoadMoreSurveyResponses && !loadingSurveyResponses ? 'pointer' : 'not-allowed'
                          }}
                          disabled={!canLoadMoreSurveyResponses || loadingSurveyResponses}
                          onClick={() => fetchSurveyResponses({
                            siteId: selectedSite,
                            flowId: selectedFlow,
                            stepId: selectedSurveyId,
                            continuationToken: surveyResponsesContinuationToken,
                            append: true
                          })}
                        >
                          {loadingSurveyResponses ? 'Loading...' : 'Load More Responses'}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              <div style={panelCardStyle}>
                <button
                  type="button"
                  style={expandToggleStyle}
                  onClick={() => setShowQuestionResponses((state) => !state)}
                >
                  <span style={panelHeaderStyle}><FaComments /> Question Responses</span>
                  {showQuestionResponses ? <FaChevronUp /> : <FaChevronDown />}
                </button>

                {showQuestionResponses && (
                  <>
                    {!selectedQuestionId ? (
                      <div style={panelEmptyStyle}>Select a question to inspect its raw responses.</div>
                    ) : loadingQuestionResponses && questionResponses.length === 0 ? (
                      <div style={panelEmptyStyle}>Loading question responses...</div>
                    ) : questionResponses.length === 0 ? (
                      <div style={panelEmptyStyle}>No responses found for the selected question.</div>
                    ) : (
                      <>
                        <div style={responseListStyle}>
                          {questionResponses.map((item, index) => (
                            <div key={`${item.sessionId}-${item.submittedAt}-${index}`} style={responseCardStyle}>
                              <div style={responseMetaStyle}>
                                <span>Session: {item.sessionId || '--'}</span>
                                <span>{formatDateTime(item.submittedAt)}</span>
                              </div>
                              <div style={responseAnswerValueStyle}>{String(item.answer ?? '--')}</div>
                              <div style={responseLocaleStyle}>Locale: {item.locale || '--'}</div>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          style={{
                            ...loadMoreBtnStyle,
                            opacity: canLoadMoreQuestionResponses && !loadingQuestionResponses ? 1 : 0.6,
                            cursor: canLoadMoreQuestionResponses && !loadingQuestionResponses ? 'pointer' : 'not-allowed'
                          }}
                          disabled={!canLoadMoreQuestionResponses || loadingQuestionResponses}
                          onClick={() => fetchQuestionResponses({
                            siteId: selectedSite,
                            flowId: selectedFlow,
                            stepId: selectedSurveyId,
                            questionId: selectedQuestionId,
                            continuationToken: questionResponsesContinuationToken,
                            append: true
                          })}
                        >
                          {loadingQuestionResponses ? 'Loading...' : 'Load More Question Responses'}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const dropdownStyles = {
  container: { position: 'relative' },
  trigger: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    fontWeight: 500,
    color: '#1e293b',
    transition: 'all 0.2s ease'
  },
  triggerText: {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    marginTop: 4,
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease-out'
  },
  searchContainer: {
    position: 'relative',
    padding: '8px 12px',
    borderBottom: '1px solid #f1f5f9',
    background: '#fcfdfe'
  },
  searchIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    fontSize: 12,
    pointerEvents: 'none'
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 28px',
    borderRadius: 6,
    border: '1px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#fff'
  },
  list: { maxHeight: '220px', overflowY: 'auto', padding: '4px' },
  option: {
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 6,
    margin: '2px 0',
    transition: 'all 0.2s ease',
    fontSize: 13
  },
  optionText: { flex: 1 },
  optionName: { fontWeight: 600, color: 'inherit' },
  optionId: { fontSize: '11px', opacity: 0.6, marginTop: 2 },
  emptyOption: { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }
};

const wrapperStyle = {
  width: '100%',
  minHeight: '100vh',
  background: '#f8fafc',
  paddingBottom: 60,
  fontFamily: 'Inter, sans-serif'
};

const containerStyle = {
  maxWidth: 1260,
  margin: '0 auto',
  padding: '24px'
};

const headerStyle = { marginBottom: 24 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = {
  width: 46,
  height: 46,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const titleStyle = { margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' };
const subtitleStyle = { marginTop: 4, fontSize: 13, color: '#64748b' };

const kpiRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 14,
  marginBottom: 24
};

const kpiCardStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: '18px 16px'
};

const kpiLabelStyle = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const kpiValueStyle = { marginTop: 8, fontSize: 26, fontWeight: 800, color: '#0f172a' };
const infoTooltipIconStyle = {
  color: '#64748b',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'help'
};

const filtersCardStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
  border: '1px solid #e2e8f0'
};

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 20,
  borderBottom: '1px solid #f1f5f9',
  paddingBottom: 12
};

const cardTitleStyle = { fontSize: 15, fontWeight: 700 };

const filtersGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
  alignItems: 'end'
};

const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' };

const primaryButtonStyle = {
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  padding: '11px 20px',
  borderRadius: 10,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};

const tableCardStyle = {
  background: '#fff',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  marginBottom: 24
};

const tableActionsHeader = {
  padding: '16px 20px',
  borderBottom: '1px solid #f1f5f9',
  background: '#fcfdfe'
};

const tableToolbarGrid = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const searchWrapper = { position: 'relative', width: 280 };
const searchIconStyle = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

const searchInputStyle = {
  width: '100%',
  padding: '8px 12px 8px 36px',
  borderRadius: 8,
  border: '1.5px solid #e2e8f0',
  fontSize: 13,
  outline: 'none',
  background: '#fff',
  color: '#3a4557'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = {
  padding: '14px 20px',
  fontSize: 10,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  textAlign: 'left'
};

const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '14px 20px', fontSize: 13, color: '#1e293b' };
const tdActionStyle = { ...tdStyle, textAlign: 'right' };
const primaryTextStyle = { fontWeight: 700, color: '#1e293b' };
const secondaryTextStyle = { marginTop: 3, fontSize: 11, color: '#64748b' };

const selectBtnStyle = {
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  padding: '6px 10px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer'
};

const emptyStateStyle = { padding: '40px 20px', textAlign: 'center', color: '#94a3b8' };

const detailSectionStyle = {
  background: '#fff',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  padding: 20,
  marginBottom: 20
};

const detailHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 };
const detailTitleStyle = { margin: 0, fontSize: 20, color: '#0f172a', fontWeight: 800 };
const detailSubtitleStyle = { marginTop: 4, color: '#64748b', fontSize: 12 };

const metricsRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  marginBottom: 16
};

const metricCardStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '12px 14px'
};

const metricLabelStyle = { fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 };
const metricValueStyle = { marginTop: 6, fontSize: 20, fontWeight: 800, color: '#0f172a' };

const surveyPanelsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(260px, 1fr) minmax(320px, 1.2fr)',
  gap: 14,
  marginBottom: 16
};

const responsesGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14
};

const panelCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  background: '#fff',
  padding: 12
};

const panelHeaderStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: '#1e293b',
  fontWeight: 800
};

const panelEmptyStyle = {
  marginTop: 12,
  padding: 16,
  borderRadius: 10,
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  color: '#64748b',
  fontSize: 12
};

const questionListStyle = { display: 'grid', gap: 8, marginTop: 12 };
const questionBtnStyle = {
  textAlign: 'left',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 10,
  background: '#fff',
  cursor: 'pointer'
};

const questionBtnActiveStyle = {
  borderColor: '#93c5fd',
  background: '#eff6ff'
};

const questionTitleStyle = { color: '#0f172a', fontSize: 13, fontWeight: 700 };
const questionMetaStyle = { marginTop: 4, color: '#64748b', fontSize: 11 };

const questionSummaryTitleStyle = {
  marginTop: 10,
  marginBottom: 8,
  fontSize: 13,
  fontWeight: 700,
  color: '#1e293b'
};

const summaryMetricsStyle = { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 };
const summaryMetricStyle = {
  fontSize: 12,
  color: '#334155',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 999,
  padding: '6px 10px'
};

const distributionTableWrapStyle = {
  maxHeight: 240,
  overflowY: 'auto',
  border: '1px solid #e2e8f0',
  borderRadius: 10
};

const distributionTableStyle = { width: '100%', borderCollapse: 'collapse' };
const distributionThStyle = {
  position: 'sticky',
  top: 0,
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  fontSize: 11,
  textAlign: 'left',
  padding: '8px 10px',
  color: '#64748b',
  textTransform: 'uppercase'
};

const distributionTdStyle = {
  borderBottom: '1px solid #f1f5f9',
  padding: '8px 10px',
  fontSize: 12,
  color: '#1e293b'
};

const trendCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  background: '#fff',
  padding: 12,
  marginBottom: 16
};

const expandToggleStyle = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  marginBottom: 8,
  padding: 0
};

const responseListStyle = {
  display: 'grid',
  gap: 10,
  maxHeight: 320,
  overflowY: 'auto',
  paddingRight: 4
};

const responseCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 10,
  background: '#fcfdff'
};

const responseMetaStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  flexWrap: 'wrap',
  fontSize: 11,
  color: '#64748b',
  marginBottom: 8
};

const responseAnswerListStyle = {
  display: 'grid',
  gap: 8
};

const responseAnswerItemStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#fff',
  padding: '8px 10px'
};

const responseAnswerQuestionStyle = { fontSize: 11, color: '#64748b' };
const responseAnswerValueStyle = { marginTop: 4, fontSize: 13, color: '#0f172a', fontWeight: 600 };
const responseLocaleStyle = { marginTop: 4, fontSize: 11, color: '#64748b' };

const loadMoreBtnStyle = {
  marginTop: 10,
  border: '1px solid #cbd5e1',
  background: '#f8fafc',
  color: '#1e293b',
  borderRadius: 8,
  padding: '8px 10px',
  fontWeight: 700,
  fontSize: 12
};


const blockingStateStyle = {
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e2e8f0',
  padding: 28,
  textAlign: 'center'
};

const blockingTitleStyle = { margin: 0, color: '#1e293b', fontSize: 20, fontWeight: 800 };
const blockingMessageStyle = { marginTop: 8, color: '#64748b', fontSize: 14 };
